import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Box,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { X as DeleteIcon, Plus as AddIcon } from 'lucide-react';

interface KanbanConfigProps {
  open: boolean;
  columns: string[];
  onClose: () => void;
  onSave: (columns: string[]) => void;
}

const KanbanConfig: React.FC<KanbanConfigProps> = ({
  open,
  columns: initialColumns,
  onClose,
  onSave,
}) => {
  const [columns, setColumns] = useState<string[]>(initialColumns);
  const [newColumnName, setNewColumnName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAddColumn = () => {
    if (!newColumnName.trim()) {
      setError('Column name cannot be empty');
      return;
    }

    if (columns.includes(newColumnName)) {
      setError('Column already exists');
      return;
    }

    setColumns([...columns, newColumnName]);
    setNewColumnName('');
    setError(null);
  };

  const handleRemoveColumn = (columnName: string) => {
    if (columns.length <= 1) {
      setError('You must have at least one column');
      return;
    }
    setColumns(columns.filter((col) => col !== columnName));
    setError(null);
  };

  const handleSave = () => {
    if (columns.length === 0) {
      setError('You must have at least one column');
      return;
    }
    onSave(columns);
  };

  const handleClose = () => {
    setColumns(initialColumns);
    setNewColumnName('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure Kanban Columns</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Current Columns
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {columns.map((col) => (
                <Chip
                  key={col}
                  label={col}
                  onDelete={() => handleRemoveColumn(col)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Add New Column
            </Typography>
            <Stack direction="row" gap={1}>
              <TextField
                placeholder="Column name"
                size="small"
                value={newColumnName}
                onChange={(e) => {
                  setNewColumnName(e.target.value);
                  setError(null);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddColumn();
                  }
                }}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleAddColumn}
                startIcon={<AddIcon size={18} />}
              >
                Add
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanConfig;
